import { getStoredAccessData, hasValidSession } from "@/publicjs/authSession";

const buildPermissionsUrl = () => {
  const baseUrl = new URL(process.env.VUE_APP_API_URL);

  baseUrl.pathname = `${baseUrl.pathname.replace(/\/$/, "")}/permisos/stream`;

  return baseUrl.toString();
};

const parseEventChunk = (chunk) => {
  const lines = chunk
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line) => line !== "" && !line.startsWith(":"));

  let event = "message";
  const dataLines = [];

  lines.forEach((line) => {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
      return;
    }

    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  });

  if (dataLines.length === 0) {
    return null;
  }

  try {
    return {
      event,
      payload: JSON.parse(dataLines.join("\n")),
    };
  } catch (error) {
    return null;
  }
};

export const openAccessPermissionStream = ({ onSnapshot, onError }) => {
  let closed = false;
  let activeController = null;
  let reconnectTimer = null;

  const cleanupPendingReconnect = () => {
    if (reconnectTimer) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    cleanupPendingReconnect();

    reconnectTimer = window.setTimeout(() => {
      if (!closed) {
        connect();
      }
    }, 3000);
  };

  const connect = async () => {
    cleanupPendingReconnect();

    if (closed || !hasValidSession()) {
      return;
    }

    const accessData = getStoredAccessData();
    const token = accessData?.token;

    if (!token) {
      return;
    }

    activeController = new AbortController();

    try {
      const response = await fetch(buildPermissionsUrl(), {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
        },
        signal: activeController.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error("access-permissions-stream-unavailable");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!closed) {
        const { value, done } = await reader.read();

        if (done) {
          throw new Error("access-permissions-stream-closed");
        }

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() ?? "";

        chunks.forEach((chunk) => {
          const parsedChunk = parseEventChunk(chunk);

          if (parsedChunk?.event === "snapshot") {
            onSnapshot?.(parsedChunk.payload);
          }
        });
      }
    } catch (error) {
      if (closed || error?.name === "AbortError") {
        return;
      }

      onError?.(error);
      scheduleReconnect();
    }
  };

  connect();

  return () => {
    closed = true;
    cleanupPendingReconnect();

    if (activeController) {
      activeController.abort();
      activeController = null;
    }
  };
};
